/**
 * Promotional Block Height Matcher
 * Matches the height of promotional blocks to other grid items in the same row
 */
class PromotionalBlockHeightMatcher {
  constructor() {
    this.grid = null;
    this.promotionalBlocks = [];
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.grid = document.querySelector('#product-grid');
    if (!this.grid) return;

    this.findPromotionalBlocks();
    this.matchHeights();
    this.setupObservers();
  }

  findPromotionalBlocks() {
    this.promotionalBlocks = Array.from(
      this.grid.querySelectorAll('.grid__item.promotional-block-wrapper')
    );
  }

  /**
   * Get all grid items in the same row as the promotional block
   */
  getRowItems(promotionalBlock) {
    const allItems = Array.from(this.grid.querySelectorAll('.grid__item'));
    const promoIndex = allItems.indexOf(promotionalBlock);
    if (promoIndex === -1) return [];

    // Get the bounding box of the promotional block
    const promoRect = promotionalBlock.getBoundingClientRect();
    const promoTop = promoRect.top;
    const promoBottom = promoRect.bottom;
    const tolerance = 10; // 10px tolerance for row detection

    // Find all items that are in the same row (similar top position)
    return allItems.filter((item) => {
      if (item === promotionalBlock) return false;
      const itemRect = item.getBoundingClientRect();
      const itemTop = itemRect.top;
      const itemBottom = itemRect.bottom;

      // Check if items overlap vertically (same row)
      return (
        Math.abs(itemTop - promoTop) < tolerance ||
        (itemTop < promoBottom && itemBottom > promoTop)
      );
    });
  }

  /**
   * Get the maximum height of grid items in the same row
   */
  getMaxRowHeight(rowItems) {
    if (rowItems.length === 0) return null;

    return Math.max(
      ...rowItems.map((item) => {
        // Get the actual rendered height
        const height = item.offsetHeight;
        return height;
      })
    );
  }

  /**
   * Match the height of a promotional block to other items in its row
   */
  matchPromotionalBlockHeight(promotionalBlock) {
    // On mobile, skip span-2 to prevent flickering (it uses fixed aspect ratio)
    const isMobile = window.innerWidth <= 989;
    const isSpan2 = promotionalBlock.classList.contains('promotional-block-wrapper--span-2');
    
    if (isMobile && isSpan2) {
      promotionalBlock.style.height = '';
      return;
    }

    const rowItems = this.getRowItems(promotionalBlock);
    const maxHeight = this.getMaxRowHeight(rowItems);

    if (maxHeight && maxHeight > 0) {
      promotionalBlock.style.height = `${maxHeight}px`;
    } else {
      // Fallback: remove height if no other items found
      promotionalBlock.style.height = '';
    }
  }

  /**
   * Match heights for all promotional blocks
   */
  matchHeights() {
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      this.findPromotionalBlocks();
      this.promotionalBlocks.forEach((block) => {
        this.matchPromotionalBlockHeight(block);
      });
    });
  }

  /**
   * Setup observers for dynamic updates
   */
  setupObservers() {
    // ResizeObserver to watch for grid item size changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.matchHeights();
      });

      // Observe all grid items
      const allItems = this.grid.querySelectorAll('.grid__item');
      allItems.forEach((item) => {
        this.resizeObserver.observe(item);
      });
    }

    // MutationObserver to watch for DOM changes (e.g., after filtering)
    if (window.MutationObserver) {
      this.mutationObserver = new MutationObserver(() => {
        // Debounce to avoid too many calls
        clearTimeout(this.matchTimeout);
        this.matchTimeout = setTimeout(() => {
          this.matchHeights();
        }, 100);
      });

      this.mutationObserver.observe(this.grid, {
        childList: true,
        subtree: true,
      });
    }

    // Window resize handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.matchHeights();
      }, 150);
    });
  }

  /**
   * Cleanup observers
   */
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}

// Initialize on page load
let promotionalBlockMatcher = null;

function initPromotionalBlockMatcher() {
  if (promotionalBlockMatcher) {
    promotionalBlockMatcher.destroy();
  }
  promotionalBlockMatcher = new PromotionalBlockHeightMatcher();
}

// Initialize immediately if DOM is ready
if (document.readyState !== 'loading') {
  initPromotionalBlockMatcher();
} else {
  document.addEventListener('DOMContentLoaded', initPromotionalBlockMatcher);
}

// Re-initialize after AJAX updates (e.g., filtering)
document.addEventListener('shopify:section:load', initPromotionalBlockMatcher);
document.addEventListener('shopify:section:reorder', initPromotionalBlockMatcher);

// Export for potential manual initialization
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PromotionalBlockHeightMatcher;
}

/**
 * Promotional Block Video Controls
 * Handles play/pause functionality for promotional block videos
 */
class PromotionalBlockVideoControls {
  constructor() {
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const promotionalBlocks = document.querySelectorAll('[data-promotional-block]');
    
    promotionalBlocks.forEach((block) => {
      const video = block.querySelector('.promotional-block__video');
      const button = block.querySelector('[data-video-control]');
      
      if (video && button) {
        this.setupVideoControl(video, button);
      }
    });
  }

  setupVideoControl(video, button) {
    // Skip if already initialized
    if (button.hasAttribute('data-video-control-initialized')) {
      return;
    }

    // Mark as initialized
    button.setAttribute('data-video-control-initialized', 'true');

    // Set initial state
    const updateButtonState = () => {
      const isPlaying = !video.paused;
      button.setAttribute('data-playing', isPlaying);
      button.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    };

    // Update button state on video events
    video.addEventListener('play', updateButtonState);
    video.addEventListener('pause', updateButtonState);
    video.addEventListener('loadedmetadata', updateButtonState);

    // Initial state
    updateButtonState();

    // Handle button click
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (video.paused) {
        video.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      } else {
        video.pause();
      }
    });
  }
}

// Initialize video controls
let promotionalBlockVideoControls = null;

function initPromotionalBlockVideoControls() {
  promotionalBlockVideoControls = new PromotionalBlockVideoControls();
}

if (document.readyState !== 'loading') {
  initPromotionalBlockVideoControls();
} else {
  document.addEventListener('DOMContentLoaded', initPromotionalBlockVideoControls);
}

// Re-initialize after AJAX updates
document.addEventListener('shopify:section:load', initPromotionalBlockVideoControls);
document.addEventListener('shopify:section:reorder', initPromotionalBlockVideoControls);

// Make function available globally for re-initialization after filter updates
window.initPromotionalBlockVideoControls = initPromotionalBlockVideoControls;

